/**
 * Laudo Analysis Validator (Frontend)
 *
 * Parses and validates LLM-generated laudo analysis responses.
 * Frontend mirror of backend/src/utils/laudoAnalysisValidator.js
 * (no calculateExpectedCost - that requires pricing matrix file access).
 */

const VALID_THERAPIES = ['Psicologia', 'Fonoaudiologia', 'Terapia Ocupacional', 'Psicopedagogia', 'Psicomotricidade'];
const SESSION_LIMITS = { min: 1, max: 7 };
const DURATION_LIMITS = { min: 30, max: 120 };
const VALID_PROBABILITIES = ['alta probabilidade', 'média probabilidade', 'baixa probabilidade'];
const VALID_SEVERITY_LEVELS = [1, 2, 3];

/**
 * Parse structured fields from LLM analysis text.
 */
export function parseLaudoAnalysis(text) {
  if (!text) {
    return { probability: null, severity: {}, therapies: [], monthlyCost: null, sections: { probability: false, severity: false, therapies: false, cost: false } };
  }

  // --- Probability ---
  let probability = null;
  for (const p of VALID_PROBABILITIES) {
    if (text.toLowerCase().includes(p)) {
      probability = p;
      break;
    }
  }

  // --- Severity levels ---
  const severity = {};
  const commMatch = text.match(/Comunica[çc][ãa]o\s*social\s*:\s*N[ií]vel\s*(\d)/i);
  if (commMatch) severity.comunicacao = parseInt(commMatch[1]);

  const behMatch = text.match(/Comportamentos\s*restritos\s*e\s*repetitivos\s*:\s*N[ií]vel\s*(\d)/i);
  if (behMatch) severity.comportamento = parseInt(behMatch[1]);

  const overallMatch = text.match(/N[ií]vel\s*de\s*gravidade\s*sugerido[^:]*:\s*(\d)/i);
  if (overallMatch) severity.overall = parseInt(overallMatch[1]);

  // --- Therapies ---
  const therapies = [];
  for (const therapy of VALID_THERAPIES) {
    const pattern = new RegExp(
      therapy + '[:\\s]+\\**\\s*(\\d+)(?:\\s*a\\s*(\\d+))?\\s*\\**\\s*sess[õo]es?\\s*semana(?:is|l)?\\s*\\(\\s*(\\d+)\\s*minutos',
      'i'
    );
    const match = text.match(pattern);
    if (match) {
      const sessionsLow = parseInt(match[1]);
      const sessionsHigh = match[2] ? parseInt(match[2]) : sessionsLow;
      const sessionsPerWeek = sessionsHigh;
      const durationMinutes = parseInt(match[3]);

      let objective = null;
      const objPattern = new RegExp(therapy + '[^]*?Objetivo\\s*:\\s*([^\\n]+)', 'i');
      const objMatch = text.match(objPattern);
      if (objMatch) objective = objMatch[1].trim();

      therapies.push({ name: therapy, sessionsPerWeek, durationMinutes, objective });
    }
  }

  // --- Monthly cost ---
  let monthlyCost = null;
  const totalLineMatch = text.match(/(?:custo|total)\s*mensal\s*(?:total|estimado)?\s*:?\s*R\$\s*([\d.,]+)/i);
  if (totalLineMatch) {
    monthlyCost = parseFloat(totalLineMatch[1].replace(/\./g, '').replace(',', '.'));
  } else {
    const allPrices = [...text.matchAll(/R\$\s*([\d.,]+)/g)];
    if (allPrices.length > 0) {
      const lastPrice = allPrices[allPrices.length - 1][1];
      const parsed = parseFloat(lastPrice.replace(/\./g, '').replace(',', '.'));
      if (parsed > 500) {
        monthlyCost = parsed;
      }
    }
  }

  // --- Section completeness ---
  const sections = {
    probability: probability !== null,
    severity: Object.keys(severity).length > 0,
    therapies: therapies.length > 0,
    cost: monthlyCost !== null
  };

  return { probability, severity, therapies, monthlyCost, sections };
}

/**
 * Validate parsed analysis and return warnings.
 */
export function validateLaudoAnalysis(parsed) {
  const warnings = [];

  if (parsed.probability === null && parsed.sections.probability === false) {
    warnings.push('Probabilidade de diagnóstico não encontrada na análise');
  }

  if (parsed.severity.comunicacao !== undefined && !VALID_SEVERITY_LEVELS.includes(parsed.severity.comunicacao)) {
    warnings.push(`Nível de comunicação social inválido: ${parsed.severity.comunicacao} (deve ser 1, 2 ou 3)`);
  }
  if (parsed.severity.comportamento !== undefined && !VALID_SEVERITY_LEVELS.includes(parsed.severity.comportamento)) {
    warnings.push(`Nível de comportamento inválido: ${parsed.severity.comportamento} (deve ser 1, 2 ou 3)`);
  }
  if (parsed.severity.overall !== undefined && !VALID_SEVERITY_LEVELS.includes(parsed.severity.overall)) {
    warnings.push(`Nível de gravidade geral inválido: ${parsed.severity.overall} (deve ser 1, 2 ou 3)`);
  }

  for (const t of parsed.therapies) {
    if (t.sessionsPerWeek < SESSION_LIMITS.min || t.sessionsPerWeek > SESSION_LIMITS.max) {
      warnings.push(`${t.name}: ${t.sessionsPerWeek} sessões/semana fora do limite (${SESSION_LIMITS.min}-${SESSION_LIMITS.max}). Possível alucinação do LLM.`);
    }
    if (t.durationMinutes < DURATION_LIMITS.min || t.durationMinutes > DURATION_LIMITS.max) {
      warnings.push(`${t.name}: duração de ${t.durationMinutes} min fora do limite (${DURATION_LIMITS.min}-${DURATION_LIMITS.max} min)`);
    }
  }

  if (!parsed.sections.severity) {
    warnings.push('Seção de classificação de gravidade não encontrada');
  }
  if (!parsed.sections.therapies) {
    warnings.push('Seção de terapias recomendadas não encontrada');
  }
  if (!parsed.sections.cost) {
    warnings.push('Custo mensal não encontrado na análise');
  }

  return { valid: warnings.length === 0, warnings };
}
